import {ArrowRightIcon, CopilotIcon, PlusIcon, ZapIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, Button, CounterLabel, Heading, IconButton, Link} from '@primer/react'
import {useCallback, useId, useRef, useState} from 'react'

import {CopilotAutomationConfigModal} from '@github-ui/copilot-automation-config'
import styles from './IssueAutomationsButton.module.css'

type SuggestedAutomation = {
  title: string
  description: string
}

type ActiveAutomation = {
  name: string
  trigger: string
}

const SUGGESTED_AUTOMATIONS: SuggestedAutomation[] = [
  {
    title: 'Label new issues',
    description: 'Apply area, type, and priority labels when issues are opened',
  },
  {
    title: 'Close stale issues',
    description: 'Auto-close issues with no activity after a warning period',
  },
]

const ACTIVE_AUTOMATIONS: ActiveAutomation[] = [
  {
    name: 'triage-agent',
    trigger: 'Issue opened',
  },
]

type IssueAutomationsButtonProps = {
  repoOwner: string
  repoName: string
}

export function IssueAutomationsButton({repoOwner, repoName}: IssueAutomationsButtonProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const headingId = useId()
  const automationsButtonRef = useRef<HTMLButtonElement>(null)

  const onOpen = useCallback(() => setIsOpen(true), [])
  const onClose = useCallback(() => setIsOpen(false), [])

  const onOpenConfigModal = useCallback(() => {
    setIsOpen(false)
    setIsConfigModalOpen(true)
  }, [])

  return (
    <>
      <AnchoredOverlay
        renderAnchor={anchorProps => (
          <Button {...anchorProps} leadingVisual={CopilotIcon} count={ACTIVE_AUTOMATIONS.length}>
            Automations
          </Button>
        )}
        open={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        width="large"
        side="outside-bottom"
        align="end"
        overlayProps={{role: 'dialog', 'aria-labelledby': headingId}}
      >
        <div className={styles.overlayContent}>
          <div className={styles.headerRow}>
            <Heading as="h3" id={headingId} className={styles.heading}>
              Issue Automations
            </Heading>
            <IconButton
              icon={PlusIcon}
              aria-label="Add automation"
              size="small"
              variant="invisible"
              onClick={onOpenConfigModal}
            />
          </div>

          <div className={styles.section}>
            <span className={styles.sectionLabel}>Suggested</span>
            <div className={styles.suggestedGrid}>
              {SUGGESTED_AUTOMATIONS.map(automation => (
                <div key={automation.title} className={styles.suggestedCard}>
                  <div className={styles.suggestedCardContent}>
                    <div className={styles.suggestedCardIcon}>
                      <ZapIcon size={16} />
                    </div>
                    <div>
                      <span className={styles.suggestedCardTitle}>{automation.title}</span>
                      <span className={styles.suggestedCardDescription}>{automation.description}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionLabelRow}>
              <span className={styles.sectionLabel}>Active automations</span>
              <CounterLabel>{ACTIVE_AUTOMATIONS.length}</CounterLabel>
            </div>
            <ActionList>
              {ACTIVE_AUTOMATIONS.map(automation => (
                <ActionList.Item key={automation.name}>
                  <ActionList.LeadingVisual>
                    <CopilotIcon size={16} />
                  </ActionList.LeadingVisual>
                  <div>
                    <span className={styles.automationName}>{automation.name}</span>
                    <span className={styles.automationTrigger}>{automation.trigger}</span>
                  </div>
                </ActionList.Item>
              ))}
            </ActionList>
          </div>

          <div className={styles.footer}>
            <Link href={`/${repoOwner}/${repoName}/agents`} muted>
              View all automations <ArrowRightIcon size={16} />
            </Link>
          </div>
        </div>
      </AnchoredOverlay>

      <CopilotAutomationConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        returnFocusRef={automationsButtonRef}
      />
    </>
  )
}
